import React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Waiting } from '@msdyn365-commerce-modules/utilities';
import { IDlvryCartProps, IDlvryCartResources } from '../dlvry-cart.props.autogenerated';
import { IDlvryCartData } from '../dlvry-cart.data';
import { getActivePricesAsync, getByLookupClausesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductLookupClause, ProductPrice, ProjectionDomain, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { formatString } from '../../../shared-utils/string-utils';

interface IImportModalProps extends IDlvryCartProps<IDlvryCartData> {
    onToggle: () => void;
}

interface IAlertProps {
    additionalClassNames?: string;
    baseClassName: string;
    message: string;
}

interface ISummaryTableProps {
    context: Msdyn365.ICoreContext;
    resources: IDlvryCartResources;
    baseClassName: string;
    importLines: IImportLine[];
    products: SimpleProduct[];
    productPrices: ProductPrice[];
    errorLines?: IErrorLine[];
}

interface IImportLine {
    sku: string;
    quantity: number;
    breakoff: boolean;
}

interface IErrorLine {
    recordId: number | undefined;
    message: string;
}

function getImportLines(input: string, resources: IDlvryCartResources): IImportLine[] {
    const lines = input.split('\n');
    if (!lines || lines.length === 0) {
        return [];
    }

    let lineIndex = 1;
    const importLines: IImportLine[] = [];
    for (const line of lines) {
        if (!line || line.startsWith('!') || line.startsWith('#')) {
            continue;
        }

        const values = line.split(/\t/);

        // Minimum required is sku and quantity
        if (values.length < 2) {
            throw new Error(formatString(resources.importInvalidNumberOfValuesError, lineIndex));
        }

        let indices: { sku: number; quantity: number; breakoff: number };

        switch (values.length) {
            case 3: // Cart export
            default:
                indices = {
                    sku: 0,
                    quantity: 1,
                    breakoff: 2
                };
                break;
            case 4: // Order template export
                indices = {
                    sku: 0,
                    quantity: 3,
                    breakoff: 4 // Not used
                };
                break;
        }

        const sku = values[indices.sku];
        const quantity = parseFloat(values[indices.quantity].replace(',', '.').trim() || '0');
        // Defaults to no if there are no third value
        const breakoffRaw = values[indices.breakoff]?.toLowerCase();

        // TODO: Determine if this error is useful or can just assume no breakoff instead
        /* if (breakoffRaw !== 'ja' && breakoffRaw !== 'nei' && breakoffRaw !== 'yes' && breakoffRaw !== 'no') {
            throw new Error(formatString(resources.importInvalidAnbrekkError, values[indices.breakoff], values[indices.sku]));
        } */

        const breakoff = breakoffRaw?.toLowerCase() === 'ja' || breakoffRaw?.toLowerCase() === 'yes';

        if (isNaN(quantity)) {
            throw new Error(formatString(resources.importInvalidQuantityForSKUError, values[indices.quantity], values[indices.sku]));
        }

        if (!quantity) {
            continue;
        }

        if (importLines.find(x => x.sku === sku)) {
            importLines.find(x => x.sku === sku)!.quantity += quantity;
        } else {
            importLines.push({ sku, quantity, breakoff });
        }

        lineIndex++;
    }

    return importLines;
}

const Alert = (props: IAlertProps) => {
    const { additionalClassNames, baseClassName, message } = props;
    return (
        <p className={classnames(`${baseClassName}__error-message`, additionalClassNames)} role='alert' aria-live='assertive'>
            <span className={`${baseClassName}__error-message__message`}>{message}</span>
        </p>
    );
};

const SummaryTable = (props: ISummaryTableProps) => {
    const { context, resources, baseClassName, importLines, products, productPrices, errorLines } = props;
    const formatOptions: Msdyn365.INumberFormatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };
    const total = context.cultureFormatter.formatNumber(products.reduce((sum, product) => {
        const quantity = importLines.find(x => x.sku === product.ItemId)?.quantity;
        const price = productPrices.find(p => p.ProductId === product.RecordId)?.CustomerContextualPrice;

        return sum + ((quantity || 0) * (price || 0));
    }, 0), formatOptions);

    const paired = importLines.map((line) => {
        return {
            importLine: line,
            product: products.find(x => x.ItemId === line.sku)
        };
    });

    const firstInput = paired.findIndex(x => !x.product);

    return (
        <table className={`${baseClassName}__summary`}>
            <thead>
                <tr>
                    <th align='left'>{resources.importProductNumberHeader}</th>
                    <th align='left'>{resources.importNameHeader}</th>
                    {!errorLines && (
                        <>
                            <th align='right'>{resources.importQuantityHeader}</th>
                            <th align='left'>{resources.importAnbrekkHeader}</th>
                            <th align='right'>{resources.importPriceHeader}</th>
                            <th align='right'>{resources.importTotalHeader}</th>
                        </>
                    )}
                    {errorLines && <th align='left'>{resources.importErrorHeader}</th>}
                </tr>
            </thead>
            <tbody>
                {paired/* .sort((a, b) => (a.product?.Name || '').localeCompare((b.product?.Name || ''))) */.map((pair, index) => {
                    const { cultureFormatter } = context;
                    const { importLine, product } = pair;

                    if (!product) {
                        if (errorLines) {
                            return null;
                        }

                        return (
                            <tr key={importLine.sku}>
                                <td>
                                    <input
                                        autoFocus={index === firstInput}
                                        onBlur={(e) => {
                                            const input = e.target as HTMLInputElement;

                                            // Don't use set state here, as it will cause tabbing to not work
                                            importLines.find(x => x.sku === importLine.sku)!.sku = input.value;
                                        }}
                                        defaultValue={importLine.sku} />
                                </td>
                                <td data-has-error>{resources.importInvalidProductsError}</td>
                                <td align='right'>{importLine.quantity}</td>
                                <td>{resources.no}</td>
                                <td align='right'></td>
                                <td align='right'></td>
                            </tr>
                        );
                    }

                    const price = productPrices.find(p => p.ProductId === product.RecordId)?.CustomerContextualPrice;
                    const total = price && importLine ? price * importLine.quantity : undefined;

                    return (
                        <tr key={importLine.sku}>
                            <td>{product.ItemId}</td>
                            <td>{product.Name}</td>
                            {!errorLines && (
                                <>
                                    <td align='right'>{importLine?.quantity}</td>
                                    <td>{resources.no}</td>
                                    <td align='right'>{price ? cultureFormatter.formatNumber(price, formatOptions) : undefined}</td>
                                    <td align='right'>{total ? cultureFormatter.formatNumber(total, formatOptions) : undefined}</td>
                                </>
                            )}
                            {errorLines && (
                                <td className={`${baseClassName}__summary__error`}>
                                    {errorLines.find(x => x.recordId === product.RecordId)?.message}
                                </td>
                            )}
                        </tr>
                    );
                })}
            </tbody>
            {!errorLines && (
                <tfoot>
                    <tr>
                        <td align='right' colSpan={5}><b>{resources.importTotalHeader}:</b></td>
                        <td align='right'>{total}</td>
                    </tr>
                </tfoot>
            )}
        </table>
    );
};

const ImportModal = (props: IImportModalProps) => {
    const { onToggle } = props;

    const [disableButtons, setDisableButtons] = React.useState<boolean>(false);
    const [progressMessage, setProgressMessage] = React.useState<string | undefined>(undefined);
    const [importLines, setImportLines] = React.useState<IImportLine[]>([]);
    const [products, setProducts] = React.useState<SimpleProduct[]>([]);
    const [productPrices, setProductPrices] = React.useState<ProductPrice[]>([]);
    const [viewType, setViewType] = React.useState<'input' | 'summary' | 'confirmation' | 'error'>('input');
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [errorLines, setErrorLines] = React.useState<IErrorLine[]>([]);
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    const baseClassName = 'dlvry-cart-import-modal';
    const heading = props.config.importModalHeading;
    const warnings = (importLines.length - products.length);

    const onValidate = async () => {
        if (!inputRef.current?.value) {
            return;
        }

        const { context } = props;

        try {
            setDisableButtons(true);
            setProgressMessage(props.resources.importProgressRetrievingProducts);

            const catalogId = context.request.apiSettings.catalogId ?? Msdyn365.getCatalogId(context.actionContext.requestContext);
            const importLines = getImportLines(inputRef.current?.value, props.resources);
            const simpleProducts = await getByLookupClausesAsync(
                { callerContext: props.context.actionContext, queryResultSettings: { Paging: { Top: 1000 } } },
                props.context.request.apiSettings.channelId,
                importLines.map(
                    line =>
                        ({
                            ItemId: line.sku
                        } as ProductLookupClause)
                ),
                catalogId
            );

            setImportLines(importLines);
            setProducts(simpleProducts);
            setProgressMessage(props.resources.importProgressRetrievingPrices);

            // Now we need to get active prices
            const projectDomain: ProjectionDomain = { ChannelId: +context.request.apiSettings.channelId, CatalogId: catalogId };
            const productPrices: ProductPrice[] = [];
            const MaxPricesPer: number = props.config.importModalPricesPer || 30;
            let count = 0;
            while (count < simpleProducts.length) {
                setProgressMessage(
                    formatString(props.resources.importProgressRetrievingPricesSection,
                        Math.min(count + MaxPricesPer, simpleProducts.length),
                        simpleProducts.length)
                );
                const prices: ProductPrice[] = await getActivePricesAsync(
                    { callerContext: props.context.actionContext, queryResultSettings: { Paging: { Top: MaxPricesPer } } },
                    projectDomain,
                    simpleProducts.slice(count, count + MaxPricesPer).map(p => p.RecordId),
                    new Date(),
                    null,
                    undefined,
                    true
                );
                productPrices.push(...prices);
                count += MaxPricesPer;
            }

            setProductPrices(productPrices);
            setViewType('summary');
        } catch (error) {
            setViewType('error');
            if (((error as unknown) as any).message) {
                setErrorMessage(((error as unknown) as any).message);
            } else {
                setErrorMessage('Unknown error.');
            }
            console.error(error);
        } finally {
            setProgressMessage(undefined);
            setDisableButtons(false);
        }
    };
    const onReValidate = async () => {
        const mergedImportLines = [...importLines].reduce<IImportLine[]>((lines, line) => {
            const existingLine = lines.find(x => x.sku === line.sku);
            if (existingLine) {
                existingLine.quantity += line.quantity;
            } else {
                lines.push(line);
            }

            return lines;
        }, []);
        setImportLines(mergedImportLines);

        const missing = mergedImportLines.filter(x => !products.find(p => p.ItemId === x.sku));
        const { context } = props;

        try {
            setDisableButtons(true);
            setProgressMessage(props.resources.importProgressRetrievingProducts);

            const catalogId = context.request.apiSettings.catalogId ?? Msdyn365.getCatalogId(context.actionContext.requestContext);
            const simpleProducts = await getByLookupClausesAsync(
                { callerContext: props.context.actionContext, queryResultSettings: { Paging: { Top: 1000 } } },
                props.context.request.apiSettings.channelId,
                missing.map(
                    line =>
                        ({
                            ItemId: line.sku
                        } as ProductLookupClause)
                ),
                catalogId
            );

            const newProducts = [...products, ...simpleProducts];
            setProducts(newProducts);
            setProgressMessage(props.resources.importProgressRetrievingPrices);

            // Now we need to get active prices
            const projectDomain: ProjectionDomain = { ChannelId: +context.request.apiSettings.channelId, CatalogId: catalogId };
            const resultProductPrices: ProductPrice[] = [];
            const MaxPricesPer: number = props.config.importModalPricesPer || 30;
            let count = 0;
            while (count < simpleProducts.length) {
                setProgressMessage(
                    formatString(props.resources.importProgressRetrievingPricesSection,
                        Math.min(count + MaxPricesPer, simpleProducts.length),
                        simpleProducts.length)
                );
                const prices: ProductPrice[] = await getActivePricesAsync(
                    { callerContext: props.context.actionContext, queryResultSettings: { Paging: { Top: MaxPricesPer } } },
                    projectDomain,
                    simpleProducts.slice(count, count + MaxPricesPer).map(p => p.RecordId),
                    new Date(),
                    null,
                    undefined,
                    true
                );
                resultProductPrices.push(...prices);
                count += MaxPricesPer;
            }

            const newProductPrices = [...productPrices, ...resultProductPrices];
            setProductPrices(newProductPrices);
            setViewType('summary');
        } catch (error) {
            setViewType('error');
            if (((error as unknown) as any).message) {
                setErrorMessage(((error as unknown) as any).message);
            } else {
                setErrorMessage('Unknown error.');
            }
            console.error(error);
        } finally {
            setProgressMessage(undefined);
            setDisableButtons(false);
        }
    };
    const onImport = async () => {
        setDisableButtons(true);
        setProgressMessage(props.resources.importProgressAddingToCartText);

        const buyInput = products.map((product) => ({
            product,
            count: importLines.find(x => x.sku === product.ItemId)!.quantity,
            customPrice: productPrices.find(x => x.ProductId === product.RecordId)?.CustomerContextualPrice
        }));
        const result = await props.data.cart.result?.addProductsToCart(buyInput);

        if (result?.status === 'SUCCESS') {
            setViewType('confirmation');
        } else if (result?.status === 'FAILED') {
            setErrorMessage(result.errorDetails?.LocalizedMessage);

            if (result.validationResults) {
                const errorLines: IErrorLine[] = [];

                for (const error of result.validationResults) {
                    // Try to find a record id
                    const recordId = error.ErrorContext?.match(/ProductId:\s(.*?)\./);

                    // If not found just skip
                    if (!recordId || recordId.length < 2) {
                        errorLines.push({
                            recordId: undefined,
                            message: error.LocalizedMessage || ''
                        });
                    } else {
                        errorLines.push({
                            recordId: Number(recordId[1]),
                            message: error.LocalizedMessage || ''
                        });
                    }
                }

                setErrorLines(errorLines);
            }

            setViewType('error');
        }

        setDisableButtons(false);
        setProgressMessage(undefined);
    };
    const onCancel = () => {
        onToggle();
    };

    const countDuplicates = (strings: string[]): Map<string, number> => {
        const stringCountMap = new Map<string, number>();

        for (const string of strings) {
            const count = stringCountMap.get(string) || 0;
            stringCountMap.set(string, count + 1);
        }

        return stringCountMap;
    };

    const handleTextAreaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            const { selectionStart, selectionEnd } = event.currentTarget;
            const value = event.currentTarget.value;
            const newValue = `${value.substring(0, selectionStart)}\t${value.substring(selectionEnd)}`;
            event.currentTarget.value = newValue;
            event.currentTarget.setSelectionRange(selectionStart + 1, selectionStart + 1);
        }
    };

    return (
        <Modal className={baseClassName} isOpen toggle={onToggle} fade={false}>
            <ModalHeader>
                <Msdyn365.Text className={`${baseClassName}__heading`} tag={heading?.tag || 'h5'} text={heading?.text} />
            </ModalHeader>
            <ModalBody>
                {viewType === 'input' && (
                    <>
                        <div dangerouslySetInnerHTML={{ __html: (props.config.importModalDescription || '') as string }} />
                        <textarea
                            ref={inputRef}
                            className={`${baseClassName}__input`}
                            placeholder={props.resources.importInputPlaceholderText}
                            rows={10}
                            onKeyDown={handleTextAreaKeyDown}
                        />
                    </>
                )}
                {viewType === 'summary' && (
                    <SummaryTable
                        context={props.context}
                        resources={props.resources}
                        baseClassName={baseClassName}
                        importLines={importLines}
                        products={products}
                        productPrices={productPrices} />
                )}
                {viewType === 'error' && (
                    <>
                        <Alert baseClassName={baseClassName} message={errorMessage || ''} />
                        {(errorLines && errorLines.length > 0 && errorLines.some(x => x.recordId)) && (
                            <SummaryTable
                                context={props.context}
                                resources={props.resources}
                                baseClassName={baseClassName}
                                importLines={importLines}
                                products={products}
                                productPrices={productPrices}
                                errorLines={errorLines} />
                        )}
                        {(errorLines && errorLines.length > 0 && errorLines.some(x => x.recordId === undefined)) && (
                            <div className={`${baseClassName}__unsorted-errors`}>
                                <div className={`${baseClassName}__unsorted-errors__description`}>{props.resources.importUnsortedErrorsDescriptionText}</div>
                                <table className={`${baseClassName}__unsorted-errors__table`}>
                                    <thead>
                                        <tr>
                                            <th align='left'>{props.resources.importUnsortedErrorsErrorHeaderText}</th>
                                            <th align='right'>{props.resources.importUnsortedErrorsQuantityHeaderText}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from(countDuplicates(errorLines.filter(x => x.recordId === undefined).map(x => x.message)))
                                            .map(([value, count]) => (
                                                <tr key={value}>
                                                    <td>{value}</td>
                                                    <td align='right'>{count}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
                {viewType === 'confirmation' && (
                    <>
                        <div>{formatString(props.resources.importAddToCartSuccessText, products.length)}</div>
                    </>
                )}
                {progressMessage && (
                    <div className={`${baseClassName}__progress-wrapper`}>
                        <Waiting className={`${baseClassName}__spinner msc-waiting-circular msc-waiting-lg`} />
                        <div className={`${baseClassName}__progress`}>{progressMessage}</div>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {viewType === 'summary' && warnings > 0 && (
                    <Alert
                        baseClassName={baseClassName}
                        additionalClassNames='warning'
                        message={warnings === 1 ? props.resources.importInvalidLinesWarningMessage : formatString(props.resources.importInvalidLinesWarningMessagePlural, warnings)}
                    />
                )}
                <div className={`${baseClassName}__btn-container`}>
                    {viewType === 'input' && (
                        <Button className={`${baseClassName}__btn-validate`} disabled={disableButtons} onClick={onValidate}>
                            {props.resources.importValidateButtonText}
                        </Button>
                    )}
                    {viewType === 'summary' && (
                        <>
                            <Button className={`${baseClassName}__btn-import`} disabled={disableButtons} onClick={onImport}>
                                {props.resources.importImportButtonText}
                            </Button>
                            {importLines.length !== products.length && (
                                <Button className={`${baseClassName}__btn-revalidate`} disabled={disableButtons} onClick={onReValidate}>
                                    {props.resources.importRevalidateButtonText}
                                </Button>
                            )}
                        </>
                    )}
                    {(viewType === 'input' || viewType === 'summary') && (
                        <Button className={`${baseClassName}__btn-cancel`} disabled={disableButtons} onClick={onCancel}>
                            {props.resources.importCancelButton}
                        </Button>
                    )}
                    {(viewType === 'confirmation' || viewType === 'error') && (
                        <Button className={`${baseClassName}__btn-close`} disabled={disableButtons} onClick={onToggle}>
                            {props.resources.importCloseButton}
                        </Button>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ImportModal;
